var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { makeObservable, observable, runInAction } from 'mobx';
import { AvailabilityCalendarService } from '@data/api';
import TimeUtils from '@business/utils/time';
import { sortObjectByKeys } from '@business/utils/object';
import { isAllotmentBlockCode } from '@business/utils/promocodes';
import Store from '@data/stores/Store';
const CACHE_SIZE = 2;
const DEFAULT_BATCH_SIZE = 2;
export default class AvailabilityCalendar {
    constructor() {
        Object.defineProperty(this, "batchSize", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: DEFAULT_BATCH_SIZE
        });
        Object.defineProperty(this, "model", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "createPayload", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (propertyId, start, end, code) => (Object.assign({ end_date: TimeUtils.format(end), property_id: propertyId, start_date: TimeUtils.format(start) }, (code
                ? isAllotmentBlockCode(code)
                    ? { allotment_block_code: code }
                    : { promo_code: code }
                : {})))
        });
        Object.defineProperty(this, "hasCodeChanged", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (newCode) => { var _a; return (((_a = this.model) === null || _a === void 0 ? void 0 : _a.code) || '') !== (newCode || ''); }
        });
        Object.defineProperty(this, "mergeData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (data, start, end) => {
                var _a;
                const defaults = Array.from(Array(TimeUtils.getDiff(end, start) + 1)).reduce((acc, _, i) => (Object.assign(Object.assign({}, acc), { [TimeUtils.toDateString(TimeUtils.getDateOffset(i, 0, 0, start))]: [] })), {});
                return sortObjectByKeys(Object.assign(Object.assign(Object.assign({}, defaults), (_a = this.model) === null || _a === void 0 ? void 0 : _a.data), data));
            }
        });
        Object.defineProperty(this, "needsToSearch", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (start, end) => {
                if (!this.model) {
                    return true;
                }
                // Interval was already requested
                const isCached = TimeUtils.isSameOrAfter(start, this.model.checkin) &&
                    TimeUtils.isSameOrBefore(end, this.model.checkout);
                return (!isCached &&
                    (TimeUtils.isSameOrBefore(start, this.model.checkin) ||
                        TimeUtils.isSameOrAfter(end, this.model.checkout)));
            }
        });
        Object.defineProperty(this, "refineInterval", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (start, end) => {
                if (!this.model) {
                    return { end, start };
                }
                // New interval is not adyacent to the model's interval and it's after the model's checkout date, complete interval
                if (TimeUtils.isAfter(start, this.model.checkout)) {
                    return { end, start: TimeUtils.getNextDay(this.model.checkout) };
                }
                // New interval is not adyacent to the model's interval and it's before the model's checkin date, complete interval
                if (TimeUtils.isBefore(end, this.model.checkin)) {
                    return { end: TimeUtils.getPrevDay(this.model.checkin), start };
                }
                // Start date is between model's checkin and checkout date, fetch only necessary data
                if (TimeUtils.isBetween(start, this.model.checkin, this.model.checkout)) {
                    return {
                        end,
                        start: TimeUtils.getNextDay(this.model.checkout),
                    };
                }
                // End date is between model's checkin and checkout date, fetch only necessary data
                if (TimeUtils.isBetween(end, this.model.checkin, this.model.checkout)) {
                    return {
                        end: TimeUtils.getPrevDay(this.model.checkin),
                        start,
                    };
                }
                // Interval doesn't overlap with model's interval, fetch all data in that interval
                return { end, start };
            }
        });
        Object.defineProperty(this, "search", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (_a) => __awaiter(this, [_a], void 0, function* ({ code, endDate, propertyId, startDate, }) {
                if (TimeUtils.isAfter(startDate, endDate)) {
                    throw new Error('AvailabilityCalendar:search - Invalid date range');
                }
                if (this.hasCodeChanged(code)) {
                    runInAction(() => {
                        this.model = null;
                    });
                }
                if (!this.needsToSearch(startDate, endDate)) {
                    return;
                }
                const { end, start } = this.refineInterval(startDate, endDate);
                const intervals = this.splitInterval(start, end);
                intervals.forEach((_a) => __awaiter(this, [_a], void 0, function* ({ end, start }) {
                    var _b, _c;
                    const payload = this.createPayload(propertyId, start, end, code);
                    const result = yield AvailabilityCalendarService.search(payload);
                    const data = this.mergeData(result.data, result.checkin, result.checkout);
                    const checkin = ((_b = this.model) === null || _b === void 0 ? void 0 : _b.checkin)
                        ? TimeUtils.min(this.model.checkin, result.checkin)
                        : result.checkin;
                    const checkout = ((_c = this.model) === null || _c === void 0 ? void 0 : _c.checkout)
                        ? TimeUtils.max(this.model.checkout, result.checkout)
                        : result.checkout;
                    runInAction(() => {
                        this.model = {
                            checkin,
                            checkout,
                            code: result.code,
                            data,
                        };
                    });
                }));
            })
        });
        Object.defineProperty(this, "searchMonths", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (_a) => __awaiter(this, [_a], void 0, function* ({ allowSameDayReservation, code, date, months = 1, propertyId, }) {
                var _b;
                const firstAvailableDate = TimeUtils.getToday();
                const isSameDayAllowed = allowSameDayReservation !== null && allowSameDayReservation !== void 0 ? allowSameDayReservation : (_b = Store.property.model) === null || _b === void 0 ? void 0 : _b.allowSameDayReservation;
                if (!isSameDayAllowed) {
                    firstAvailableDate.setDate(firstAvailableDate.getDate() + 1);
                }
                const startOfDateMonth = TimeUtils.getStartOfMonthDate(date);
                const startDate = TimeUtils.isBefore(startOfDateMonth, firstAvailableDate)
                    ? firstAvailableDate
                    : startOfDateMonth;
                const endDate = TimeUtils.getEndOfMonthDate(date, months + CACHE_SIZE);
                yield this.search({
                    code,
                    endDate,
                    propertyId,
                    startDate,
                });
            })
        });
        Object.defineProperty(this, "splitInterval", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (startDate, endDate) => {
                if (this.batchSize === 0) {
                    return [{ end: endDate, start: startDate }];
                }
                const monthDiff = TimeUtils.getMonthDiff(endDate, startDate) + 1;
                const batches = Math.ceil(monthDiff / this.batchSize);
                const intervals = [];
                for (let i = 0; i < batches; i++) {
                    const start = TimeUtils.getStartOfMonthDate(TimeUtils.getDateOffset(0, this.batchSize * i, 0, startDate));
                    const end = TimeUtils.getEndOfMonthDate(TimeUtils.getDateOffset(0, this.batchSize - 1, 0, start));
                    intervals.push({
                        end: TimeUtils.isAfter(end, endDate) ? endDate : end,
                        start: TimeUtils.isBefore(start, startDate) ? startDate : start,
                    });
                }
                return intervals;
            }
        });
        makeObservable(this);
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], AvailabilityCalendar.prototype, "model", void 0);
